/** @format */

// ─── Declotr Admin ─── Chat Abuse / Mass-DM Tooling ───
// Surfaces senders who blasted DMs to many recipients (impersonation /
// phishing pattern) and lets an admin block + purge them, mark trusted
// support accounts as official, and notify victims.

import React, { useEffect, useState, useCallback } from "react";
import { apiInstanceFetch } from "../../../util/api";
import { warningForText } from "../../../util/Alert";
import { toast } from "react-toastify";

const sectionStyle = { background: "#fff", borderRadius: 8, padding: 16, marginBottom: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" };
const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: 14 };
const thtd = { textAlign: "left", padding: "8px 10px", borderBottom: "1px solid #eee", verticalAlign: "top" };
const btn = { border: "none", borderRadius: 6, padding: "6px 12px", cursor: "pointer", marginRight: 6, fontSize: 13 };
const btnPrimary = { ...btn, background: "#3b82f6", color: "#fff" };
const btnDanger = { ...btn, background: "#dc2626", color: "#fff" };
const btnGhost = { ...btn, background: "#f3f4f6", color: "#111" };
const inputStyle = { padding: "6px 10px", borderRadius: 6, border: "1px solid #d1d5db", marginRight: 8 };
const badge = (bg, color) => ({ display: "inline-block", padding: "2px 8px", borderRadius: 12, background: bg, color, fontSize: 11, fontWeight: 600 });

const ChatAbuse = () => {
	const [days, setDays] = useState(7);
	const [minRecipients, setMinRecipients] = useState(3);
	const [onlyImages, setOnlyImages] = useState(false);
	const [senders, setSenders] = useState([]);
	const [loading, setLoading] = useState(false);

	const [selectedUser, setSelectedUser] = useState(null);
	const [userMessages, setUserMessages] = useState([]);

	const fetchSenders = useCallback(async () => {
		setLoading(true);
		try {
			const url = `chat/admin/massDMSenders?days=${days}&minRecipients=${minRecipients}&onlyImages=${onlyImages}`;
			const res = await apiInstanceFetch.get(url);
			if (res?.status) setSenders(res.senders || []);
			else toast.error(res?.message || "Failed to load");
		} catch (e) {
			toast.error(e.message || "Network error");
		}
		setLoading(false);
	}, [days, minRecipients, onlyImages]);

	useEffect(() => {
		fetchSenders();
	}, [fetchSenders]);

	const viewMessages = async (userId) => {
		try {
			const res = await apiInstanceFetch.get(`chat/admin/userMessages?userId=${userId}&limit=200`);
			if (res?.status) {
				setSelectedUser(userId);
				setUserMessages(res.messages || []);
			} else toast.error(res?.message || "Failed");
		} catch (e) {
			toast.error(e.message || "Network error");
		}
	};

	const blockAndPurge = async (userId) => {
		const ok = await warningForText("Block this user, delete all their chat messages and conversations? This cannot be undone.");
		if (!ok) return;

		try {
			const res = await apiInstanceFetch.post("chat/admin/blockAndPurge", JSON.stringify({ userId, purge: true }));
			if (res?.status) {
				toast.success(`Blocked. Removed ${res.deletedMessages} messages, ${res.deletedChats} chats.`);

				// Auto-offer to warn victims
				const victims = res.victims || [];
				if (victims.length > 0) {
					const sendIt = window.confirm(`Send a security warning to ${victims.length} affected user(s)?`);
					if (sendIt) await sendWarning(victims);
				}

				fetchSenders();
				if (selectedUser === userId) {
					setSelectedUser(null);
					setUserMessages([]);
				}
			} else toast.error(res?.message || "Failed");
		} catch (e) {
			toast.error(e.message || "Network error");
		}
	};

	const sendWarning = async (userIds) => {
		try {
			const res = await apiInstanceFetch.post("chat/admin/sendSecurityWarning", JSON.stringify({ userIds }));
			if (res?.status) toast.success(`Warning sent to ${res.count} user(s)`);
			else toast.error(res?.message || "Failed");
		} catch (e) {
			toast.error(e.message || "Network error");
		}
	};

	const setOfficial = async (userId, isOfficial) => {
		try {
			const res = await apiInstanceFetch.post("chat/admin/setOfficial", JSON.stringify({ userId, isOfficial }));
			if (res?.status) {
				toast.success(isOfficial ? "Marked as Official" : "Removed Official flag");
				fetchSenders();
			} else toast.error(res?.message || "Failed");
		} catch (e) {
			toast.error(e.message || "Network error");
		}
	};

	return (
		<div style={{ padding: 16 }}>
			<div style={sectionStyle}>
				<h4 style={{ margin: 0, marginBottom: 12 }}>Chat Abuse / Mass-DM Detection</h4>
				<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
					<label style={{ marginRight: 8 }}>Days:</label>
					<input type='number' min='1' max='90' value={days} onChange={(e) => setDays(parseInt(e.target.value) || 7)} style={{ ...inputStyle, width: 80 }} />
					<label style={{ marginRight: 8 }}>Min recipients:</label>
					<input type='number' min='2' value={minRecipients} onChange={(e) => setMinRecipients(parseInt(e.target.value) || 3)} style={{ ...inputStyle, width: 80 }} />
					<label style={{ marginRight: 16 }}>
						<input type='checkbox' checked={onlyImages} onChange={(e) => setOnlyImages(e.target.checked)} style={{ marginRight: 6 }} />
						Image messages only
					</label>
					<button style={btnPrimary} onClick={fetchSenders} disabled={loading}>
						{loading ? "Loading..." : "Refresh"}
					</button>
				</div>
				<p style={{ color: "#6b7280", fontSize: 12, margin: 0 }}>
					Senders below have messaged at least {minRecipients} different people in the past {days} day(s). High receiver counts often indicate impersonation or phishing.
				</p>
			</div>

			<div style={sectionStyle}>
				<h5 style={{ marginTop: 0 }}>Suspect Senders ({senders.length})</h5>
				<div style={{ overflowX: "auto" }}>
					<table style={tableStyle}>
						<thead>
							<tr style={{ background: "#f9fafb" }}>
								<th style={thtd}>User</th>
								<th style={thtd}>Email</th>
								<th style={thtd}>Recipients</th>
								<th style={thtd}>Messages</th>
								<th style={thtd}>Images</th>
								<th style={thtd}>First → Last</th>
								<th style={thtd}>Sample</th>
								<th style={thtd}>Status</th>
								<th style={thtd}>Actions</th>
							</tr>
						</thead>
						<tbody>
							{senders.map((s) => {
								const u = s.user || {};
								const name = `${u.firstName || ""} ${u.lastName || ""}`.trim() || "—";
								return (
									<tr key={s._id}>
										<td style={thtd}>
											<div style={{ fontWeight: 600 }}>{name}</div>
											<div style={{ color: "#6b7280", fontSize: 11 }}>{s._id}</div>
										</td>
										<td style={thtd}>{u.email || "—"}</td>
										<td style={thtd}>
											<span style={badge("#fee2e2", "#991b1b")}>{s.receiverCount}</span>
										</td>
										<td style={thtd}>{s.messageCount}</td>
										<td style={thtd}>{s.imageCount}</td>
										<td style={thtd}>
											<div style={{ fontSize: 11 }}>
												{s.firstAt ? new Date(s.firstAt).toLocaleString() : "—"}
												<br />
												{s.lastAt ? new Date(s.lastAt).toLocaleString() : "—"}
											</div>
										</td>
										<td style={{ ...thtd, maxWidth: 240, fontSize: 12, color: "#374151" }}>{(s.sampleText || "").slice(0, 120)}</td>
										<td style={thtd}>
											{u.isBlock && <span style={{ ...badge("#fee2e2", "#991b1b"), marginRight: 4 }}>BLOCKED</span>}
											{u.isOfficial && <span style={{ ...badge("#dbeafe", "#1e40af"), marginRight: 4 }}>OFFICIAL</span>}
											{u.isSeller && <span style={badge("#dcfce7", "#166534")}>SELLER</span>}
										</td>
										<td style={thtd}>
											<button style={btnGhost} onClick={() => viewMessages(s._id)}>
												View
											</button>
											{!u.isOfficial ? (
												<button style={btnGhost} onClick={() => setOfficial(s._id, true)}>
													Mark Official
												</button>
											) : (
												<button style={btnGhost} onClick={() => setOfficial(s._id, false)}>
													Unmark
												</button>
											)}
											{!u.isBlock && (
												<button style={btnDanger} onClick={() => blockAndPurge(s._id)}>
													Block + Purge
												</button>
											)}
										</td>
									</tr>
								);
							})}
							{senders.length === 0 && !loading && (
								<tr>
									<td style={thtd} colSpan={9}>
										No suspicious senders in the selected window. 🎉
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</div>

			{selectedUser && (
				<div style={sectionStyle}>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
						<h5 style={{ margin: 0 }}>Messages from {selectedUser}</h5>
						<button
							style={btnGhost}
							onClick={() => {
								setSelectedUser(null);
								setUserMessages([]);
							}}>
							Close
						</button>
					</div>
					<div style={{ maxHeight: 400, overflowY: "auto" }}>
						<table style={tableStyle}>
							<thead>
								<tr style={{ background: "#f9fafb" }}>
									<th style={thtd}>When</th>
									<th style={thtd}>Type</th>
									<th style={thtd}>To</th>
									<th style={thtd}>Text / Image</th>
								</tr>
							</thead>
							<tbody>
								{userMessages.map((m) => {
									const r = m.receiverId || {};
									return (
										<tr key={m._id}>
											<td style={thtd}>{new Date(m.createdAt).toLocaleString()}</td>
											<td style={thtd}>{m.type}</td>
											<td style={thtd}>{`${r.firstName || ""} ${r.lastName || ""}`.trim() || r._id || "—"}</td>
											<td style={thtd}>
												{m.text || ""}
												{m.image && <div style={{ marginTop: 4, color: "#6b7280", fontSize: 11 }}>📎 {m.image}</div>}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				</div>
			)}
		</div>
	);
};

export default ChatAbuse;
